import { Box, Paper, Typography, Button, Tooltip } from '@mui/material'
import { Close } from '@mui/icons-material'
import { theme } from '@/styles/theme'
import { Document, EntityType } from '@/types'
import { useDeleteDocument } from '@/api/accounting-dealer/file/delete-document'
import { queryClient } from '@/api/react-query'
import { useState } from 'react'
import { PopupDialog } from '../popup-dialog'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'

interface DocumentPreviewProps {
  file: Document
  previewUrl: string
  entity: { key: string; type: EntityType }
}

export const DocumentPreview = ({
  file,
  previewUrl,
  entity,
}: DocumentPreviewProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { mutate: deleteDocument, isPending: loadingDelete } =
    useDeleteDocument({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['documents', entity.key, entity.type],
        })

        setDeleteDialogOpen(false)
      },
    })

  const onRequestDelete = (e: React.MouseEvent) => {
    setDeleteDialogOpen(true)
    e.stopPropagation()
  }

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          position: 'relative',
          width: 100,
          height: 100,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {file.mime_type === 'application/pdf' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 28, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {file.original_name.slice(0, 8)}...
              </Typography>
            </Box>
          ) : (
            <Box
              component="img"
              src={previewUrl}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              alt={file.original_name}
            />
          )}
        </Box>
        <Tooltip title="Remover arquivo">
          <Button
            fullWidth={false}
            onClick={(e) => onRequestDelete(e)}
            sx={{
              minWidth: 18,
              minHeight: 18,
              my: 0,
              position: 'absolute',
              px: 0,
              py: 0,
              top: -8,
              right: -8,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              color: theme.palette.error.main,
              cursor: 'pointer',
              transition: 'all 0.5s ease',
              ':hover': {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.background.paper,

                px: 0.5,
                py: 0.5,
              },
            }}
            variant="contained"
          >
            <Close
              fontSize="medium"
              sx={{
                color: 'inherit',
              }}
            />
          </Button>
        </Tooltip>
      </Paper>
      <PopupDialog
        open={deleteDialogOpen}
        callbacks={{
          onConfirm: () => {
            deleteDocument({ document_key: file.key })
          },
          onClose: () => setDeleteDialogOpen(false),
        }}
        title={'Remover arquivo'}
        message={'Deseja remover o arquivo?'}
        buttonTexts={{
          cancel: 'Cancelar',
          confirm: 'Remover',
        }}
        destructive
        confirmLoading={loadingDelete}
      />
    </>
  )
}
